import type { AxiosError } from 'axios';
import { create } from 'zustand';

import {
  getWorkspace,
  type GetWorkspaceResponse,
} from '@/entities/workspace/api';
import service, { setDefaultTimeZone } from '@/services/index';
import CreateWorkspaceError from '@/services/workspace/CreateWorkspaceErrorHandler';
import { updateSentryWorkspace } from '@/shared/lib/sentry';
import useCommonStore from '@/stores/pure/CommonStore';
import { SOCKET_SYNC_TYPE } from '@/types/common';
import { LANGUAGE } from '@/types/commonConst';

import useColumnConfigStore from './DataTable/ColumnConfigStore';
import useUserStore from './UserStore';

export const memberRoleDic: RoleDicT = {
  1: 'Viewer',
  2: 'Editor',
};

type State = {
  workspaces: WorkspaceListItemT[];
  currWorkspace: WorkspaceT | null;
  currWorkspaceMembers: MemberT[];
  workspaceFieldOptions: WorkspaceFieldOptions;
  workspaceStorage: Record<string, any>;
};

type Action = {
  updateWorkspaceFieldOptions: (
    type: SOCKET_SYNC_TYPE,
    field: WorkspaceConfigsFields,
    value: string,
  ) => Promise<void>;
  setWorkspaceFieldOptions: (
    type: SOCKET_SYNC_TYPE,
    field: WorkspaceConfigsFields,
    value: string,
  ) => void;
  setMultipleWorkspaceFieldOptions: (
    type: SOCKET_SYNC_TYPE,
    field: WorkspaceConfigsFields,
    values: string[],
  ) => void;
  setCurrWorkspace: (workspaces: WorkspaceT | null) => void;
  regenerationWorkspaceKey: (isViewer: boolean) => Promise<string>;
  createWorkspace: (
    params: CreateWorkspaceParams,
  ) => Promise<{ flag: boolean; message: string; alias?: string }>;
  fetchWorkspaces: () => Promise<WorkspaceListItemT[]>;
  fetchWorkspace: (alias: string, request?: () => void) => Promise<void>;
  fetchWorkspaceMembers: () => void;
  changeWorkspaceName: (name: string) => Promise<boolean | string>;
  changeWorkspaceTimezone: (timezone: string) => void;
  fetchWorkspaceAPI: () => void;
  leaveWorkspace: () => Promise<boolean | string>;
  deleteWorkspaceAPI: () => void;
  deleteCurrWorkspace: () => Promise<boolean | string>;
  initWorkspaceStore: () => void;
  changeMemberRole: (user_id: string, role: MEMBER_ROLE) => void;
  kickMember: (user_id: string) => void;
  updateAnonymization: (state: boolean) => Promise<void>;
  updatePasswordProtection: (state: boolean) => Promise<void>;
  updateMockDateAsWorkspaceMem: (state: MemberT) => void;
  updateLanguageSetting: (value: LANGUAGE) => void;

  setWorkspaceAttr: <K extends keyof WorkspaceT>(
    alias: string,
    field: K,
    value: WorkspaceT[K],
  ) => void;

  setWorkspaceStorageItem: (key: string, value: any) => void;
};

const initialWorkspaceConfigs: WorkspaceFieldOptions = {
  account_type_values: [],
  contact_type_values: [],
  data_origin_values: [],
  industry_values: [],
  job_function_values: [],
  contact_plan_values: [],
  contact_stage_values: [],
  plan_values: [],
  rating_values: [],
  role_values: [],
  team_values: [],
  size_values: [],
  account_stage_values: [],
  account_primary_usecase_values: [],
};

const initialState: State = {
  workspaces: [],
  currWorkspace: null,
  currWorkspaceMembers: [],
  workspaceFieldOptions: initialWorkspaceConfigs,
  workspaceStorage: {},
};

const initialWorkspaceConfig: WorkspaceConfigT = {
  scraper_names: [],
};

const normalizeWorkspaceConfig = (
  config?: GetWorkspaceResponse['config'] | WorkspaceT['config'],
): WorkspaceT['config'] => ({
  scraper_names: config?.scraper_names ?? initialWorkspaceConfig.scraper_names,
});

const getWorkspaceCredentials = (
  currWorkspace: WorkspaceT | null,
  alias: string,
) => {
  if (currWorkspace?.alias !== alias) {
    return {
      api_created_by: undefined,
      api_key: undefined,
      api_secret: undefined,
    };
  }

  return {
    api_created_by: currWorkspace.api_created_by,
    api_key: currWorkspace.api_key,
    api_secret: currWorkspace.api_secret,
  };
};

const normalizeWorkspaceDetail = (
  data: GetWorkspaceResponse,
  currWorkspace: WorkspaceT | null,
): WorkspaceT => ({
  activated_enterprise: !!data.activated_enterprise,
  alias: data.alias,
  data_anonymization: data.data_anonymization,
  password_protection: data.password_protection,
  language: (data.language as LANGUAGE | undefined) ?? LANGUAGE.EN,
  name: data.name ?? '',
  timezone: data.timezone ?? '',
  config: normalizeWorkspaceConfig(data.config),
  saved_taxonomy_id: data.saved_taxonomy_id ?? '',
  ...getWorkspaceCredentials(currWorkspace, data.alias),
});

const normalizeWorkspaceFieldOptions = (
  data: Pick<GetWorkspaceResponse, keyof WorkspaceFieldOptions>,
): WorkspaceFieldOptions => ({
  account_type_values: data.account_type_values || [],
  contact_type_values: data.contact_type_values || [],
  data_origin_values: data.data_origin_values || [],
  industry_values: data.industry_values || [],
  job_function_values: data.job_function_values || [],
  contact_plan_values: data.contact_plan_values || [],
  contact_stage_values: data.contact_stage_values || [],
  plan_values: data.plan_values || [],
  rating_values: data.rating_values || [],
  role_values: data.role_values || [],
  team_values: data.team_values || [],
  size_values: data.size_values || [],
  account_stage_values: data.account_stage_values || [],
  account_primary_usecase_values: data.account_primary_usecase_values || [],
});

const useWorkspaceStore = create<State & Action>((set, get) => ({
  ...initialState,
  updateWorkspaceFieldOptions: async (type, field, value) => {
    switch (type) {
      case SOCKET_SYNC_TYPE.CREATE: {
        await service.workspace.createWorkspaceFieldOption(field, value);
        get().setWorkspaceFieldOptions(type, field, value);
        break;
      }
      case SOCKET_SYNC_TYPE.DELETE: {
        await service.workspace.deleteWorkspaceFieldOption(field, value);
        get().setWorkspaceFieldOptions(type, field, value);
        break;
      }
      default: {
        console.error(
          `${type} is invalid SOCKET_SYNC_TYPE for workspace field options`,
        );
        break;
      }
    }
  },
  setWorkspaceFieldOptions: (type, field, value) => {
    const newConfigs = { ...get().workspaceFieldOptions };
    switch (type) {
      case SOCKET_SYNC_TYPE.CREATE: {
        if (newConfigs[`${field}_values`]) {
          newConfigs[`${field}_values`].push(value);
          newConfigs[`${field}_values`] = [
            ...new Set(newConfigs[`${field}_values`]),
          ];
        } else {
          newConfigs[`${field}_values`] = [value];
        }
        set({ workspaceFieldOptions: newConfigs });
        break;
      }
      case SOCKET_SYNC_TYPE.DELETE: {
        newConfigs[`${field}_values`] = newConfigs[`${field}_values`].filter(
          (v) => v !== value,
        );
        set({ workspaceFieldOptions: newConfigs });
        break;
      }
      default: {
        console.error(
          `${type} is invalid SOCKET_SYNC_TYPE for workspace field options`,
        );
        break;
      }
    }
  },
  setMultipleWorkspaceFieldOptions: (type, field, values = []) => {
    const newConfigs = { ...get().workspaceFieldOptions };
    switch (type) {
      case SOCKET_SYNC_TYPE.CREATE: {
        values.forEach((value) => {
          newConfigs[`${field}_values`].push(value);
        });
        set({ workspaceFieldOptions: newConfigs });
        break;
      }
      case SOCKET_SYNC_TYPE.DELETE: {
        const newOptions = [...newConfigs[`${field}_values`]];
        values.forEach((value) => {
          const targetIndex = newOptions.findIndex((el) => el === value);
          newOptions.splice(targetIndex, 1);
        });
        set({ workspaceFieldOptions: newConfigs });
        break;
      }
      default: {
        console.error(
          `${type} is invalid SOCKET_SYNC_TYPE for workspace field options`,
        );
        break;
      }
    }
  },
  setCurrWorkspace: (workspaceInfo) => {
    const { user } = useUserStore.getState();
    if (user) {
      if (workspaceInfo) {
        window.localStorage.setItem(
          `curr_workspace_${user.user_id}`,
          workspaceInfo.alias,
        );
        updateSentryWorkspace(workspaceInfo.alias, workspaceInfo.name);
      } else {
        window.localStorage.removeItem(`curr_workspace_${user.user_id}`);
      }
    }
    if (workspaceInfo?.timezone) {
      setDefaultTimeZone(workspaceInfo.timezone);
    }
    set({ currWorkspace: workspaceInfo });
  },
  regenerationWorkspaceKey: async (isViewer) => {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    const data = await service.workspace.patchWorkspaceAlias(
      currWorkspace.alias,
      isViewer,
    );
    return data.invite_code;
  },
  createWorkspace: async (params) => {
    try {
      const data = await service.workspace.createWorkspace(params);
      const workspaces = await service.workspace.fetchWorkspaces();
      const { updateHasWorkspaceStatus } = useUserStore.getState();
      updateHasWorkspaceStatus(true);
      const workspaceInfo: WorkspaceT = {
        activated_enterprise: data.activated_enterprise,
        language: data.language,
        alias: data.alias,
        name: data.name,
        timezone: data.timezone,
        data_anonymization: data.data_anonymization,
        password_protection: data.password_protection,
        config: normalizeWorkspaceConfig(data.config),
        saved_taxonomy_id: data.saved_taxonomy_id ?? '',
      };
      set((state) => ({
        ...state,
        workspaces: workspaces,
        currWorkspace: workspaceInfo,
      }));
      return { flag: true, message: '', alias: data.alias };
    } catch (e) {
      if (e instanceof CreateWorkspaceError) {
        throw e.handle();
      }
      throw { flag: false, message: 'Unknown Error' };
    }
  },
  fetchWorkspaces: async () => {
    const data = await service.workspace.fetchWorkspaces();
    set({ workspaces: data });
    return data;
  },
  fetchWorkspace: async (alias, request) => {
    try {
      const data = await getWorkspace({ workspace_id: alias });
      const { user } = useUserStore.getState();
      if (!user) return;
      if (data) {
        window.localStorage.setItem(
          `curr_workspace_${user.user_id}`,
          data.alias,
        );
      } else {
        window.localStorage.removeItem(`curr_workspace_${user.user_id}`);
      }
      const currWorkspace = get().currWorkspace;
      const normalizedWorkspace = normalizeWorkspaceDetail(data, currWorkspace);
      set({
        currWorkspace: normalizedWorkspace,
        workspaceFieldOptions: normalizeWorkspaceFieldOptions(data),
      });
      if (normalizedWorkspace.timezone) {
        setDefaultTimeZone(normalizedWorkspace.timezone);
      }
      useColumnConfigStore.getState().getColumnConfigs();
    } catch (e) {
      console.error(e);
      request?.();
      return;
    }
  },
  fetchWorkspaceMembers: async () => {
    const workspaceAlias = get().currWorkspace?.alias;
    if (!workspaceAlias) return;
    const data = await service.workspace.fetchWorkspaceMembers(workspaceAlias);
    set({ currWorkspaceMembers: data });
  },
  fetchWorkspaceAPI: async () => {
    const newConfigs = get().currWorkspace ? get().currWorkspace : null;
    const workspaceAlias = get().currWorkspace?.alias;
    if (!workspaceAlias) return;

    const { api_key, api_secret, api_created_by } =
      await service.workspace.fetchCredentialAPIKey(workspaceAlias);

    set({
      currWorkspace: newConfigs
        ? {
            ...newConfigs,
            api_created_by: api_created_by,
            api_key: api_key,
            api_secret: api_secret,
          }
        : null,
    });
  },

  deleteWorkspaceAPI: async () => {
    const newConfigs = get().currWorkspace ? get().currWorkspace : null;
    const workspaceAlias = get().currWorkspace?.alias;
    if (!workspaceAlias) return;

    await service.workspace.removeCredentialAPIKey(workspaceAlias);

    set({
      currWorkspace: newConfigs
        ? {
            ...newConfigs,
            api_created_by: undefined,
            api_key: undefined,
            api_secret: undefined,
          }
        : null,
    });
  },
  changeWorkspaceName: async (name) => {
    const { workspaces, currWorkspace, setCurrWorkspace } = get();
    const workspaceAlias = currWorkspace?.alias;
    if (!workspaceAlias) return false;

    try {
      await service.workspace.patchWorkspaceName(workspaceAlias, name);

      const newWorkspaces = [...workspaces];
      const index = newWorkspaces.findIndex(
        (workspace) => workspace.alias === workspaceAlias,
      );
      const targetWorkspace = newWorkspaces[index];
      targetWorkspace.name = name;
      newWorkspaces.upsert(
        targetWorkspace,
        (el) => el.alias === targetWorkspace.alias,
      );

      const newCurrWorkspace = {
        ...currWorkspace,
        name,
      };

      set({ workspaces: newWorkspaces });
      setCurrWorkspace(newCurrWorkspace);

      return true;
    } catch (e) {
      const exception = e as AxiosError;

      // Error handling rules:
      // If 'status' key is in response result:
      //    1. Status === 409 when the given name already exists
      let error = '';
      if (exception.response) {
        if (exception.response.status === 409) {
          error =
            'The name already exists. Select different name for the workspace.';
        } else {
          error = 'Unknown error.';
        }
      }

      return error;
    }
  },
  changeWorkspaceTimezone: async (timezone) => {
    const { currWorkspace, setCurrWorkspace } = get();
    const workspaceAlias = currWorkspace?.alias;
    if (!workspaceAlias) return;

    await service.workspace.patchWorkspaceTimezone(workspaceAlias, timezone);

    const newWorkspace = {
      ...currWorkspace,
      timezone,
    };

    setCurrWorkspace(newWorkspace);
  },
  leaveWorkspace: async () => {
    const { workspaces, currWorkspace, setCurrWorkspace } = get();
    const { initStoreWhenChangeWorkspace } = useCommonStore.getState();
    const { updateHasWorkspaceStatus } = useUserStore.getState();

    const workspaceAlias = currWorkspace?.alias;
    if (!workspaceAlias) return false;

    try {
      await service.workspace.leaveWorkspaceMember(workspaceAlias);

      const newWorkspaces = workspaces.filter(
        (workspace) => workspace.alias !== workspaceAlias,
      );
      set({
        workspaces: newWorkspaces,
      });

      const newCurrWorkspace =
        newWorkspaces.length > 0 ? newWorkspaces[0] : null;
      const newCurrWorkspaceAlias = newCurrWorkspace?.alias;

      if (newCurrWorkspaceAlias) {
        const newCurrWorkspaceData = await getWorkspace({
          workspace_id: newCurrWorkspaceAlias,
        });
        setCurrWorkspace(
          normalizeWorkspaceDetail(newCurrWorkspaceData, null),
        );
      }

      if (newWorkspaces.length === 0) updateHasWorkspaceStatus(false);

      initStoreWhenChangeWorkspace();
      const redirectUrl = newCurrWorkspace
        ? `/workspace/${newCurrWorkspace.alias}`
        : '/create-workspace';

      return redirectUrl;
    } catch (e) {
      return false;
    }
  },
  deleteCurrWorkspace: async () => {
    const { workspaces, currWorkspace, setCurrWorkspace } = get();
    const { initStoreWhenChangeWorkspace } = useCommonStore.getState();
    const { updateHasWorkspaceStatus } = useUserStore.getState();

    const workspaceAlias = currWorkspace?.alias;
    if (!workspaceAlias) return false;

    try {
      await service.workspace.deleteWorkspace(workspaceAlias);

      const newWorkspaces = workspaces.filter(
        (workspace) => workspace.alias !== workspaceAlias,
      );
      set({
        workspaces: newWorkspaces,
      });

      const newCurrWorkspace =
        newWorkspaces.length > 0 ? newWorkspaces[0] : null;
      const newCurrWorkspaceAlias = newCurrWorkspace?.alias;

      if (newCurrWorkspaceAlias) {
        const newCurrWorkspaceData = await getWorkspace({
          workspace_id: newCurrWorkspaceAlias,
        });
        setCurrWorkspace(
          normalizeWorkspaceDetail(newCurrWorkspaceData, null),
        );
      }

      if (newWorkspaces.length === 0) updateHasWorkspaceStatus(false);

      initStoreWhenChangeWorkspace();
      const redirectUrl = newCurrWorkspace
        ? `/workspace/${newCurrWorkspace.alias}`
        : '/create-workspace';

      return redirectUrl;
    } catch (e) {
      return false;
    }
  },

  changeMemberRole(user_id, role) {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    service.workspace.patchWorkspaceMemberRole(
      currWorkspace.alias,
      user_id,
      role,
    );
    set((state) => ({
      currWorkspaceMembers: state.currWorkspaceMembers.map((member) => {
        if (member.user_id === user_id) {
          return { ...member, role };
        }
        return member;
      }),
    }));
  },
  kickMember(user_id) {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    service.workspace.kickWorkspaceMember(currWorkspace.alias, user_id);
    set((state) => ({
      currWorkspaceMembers: state.currWorkspaceMembers.filter(
        (member) => member.user_id !== user_id,
      ),
    }));
  },
  async updateAnonymization(state) {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    await service.workspace.updateAnonymizationSetting(state);
    set({
      currWorkspace: {
        ...currWorkspace,
        data_anonymization: state,
      },
    });
  },
  async updatePasswordProtection(state) {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    await service.workspace.updatePasswordProtection(state);
    set({
      currWorkspace: {
        ...currWorkspace,
        password_protection: state,
      },
    });
  },
  async updateLanguageSetting(value) {
    const { currWorkspace } = get();
    if (!currWorkspace) return;
    await service.workspace.updateLanguageSetting(value);
    set({
      currWorkspace: {
        ...currWorkspace,
        language: value,
      },
    });
  },
  updateMockDateAsWorkspaceMem(state: MemberT) {
    const { currWorkspaceMembers } = get();
    set({
      currWorkspaceMembers: [...currWorkspaceMembers, state],
    });
  },

  setWorkspaceStorageItem(key, value) {
    set((prev) => ({
      ...prev,
      workspaceStorage: {
        ...prev.workspaceStorage,
        [key]: value,
      },
    }));
  },

  setWorkspaceAttr(alias, field, value) {
    set((prev) => {
      if (prev.currWorkspace?.alias !== alias) return prev;
      const workspace: WorkspaceT = {
        ...prev.currWorkspace,
        [field]: value,
      };
      return {
        ...prev,
        currWorkspace: workspace,
      };
    });
  },

  initWorkspaceStore() {
    set({ ...initialState });
  },
}));

export default useWorkspaceStore;
